import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlignLeft,
  Bath,
  Bed,
  CheckSquare,
  ChevronRight,
  Copy,
  DollarSign,
  Edit,
  GripVertical,
  Hash,
  Home,
  List,
  Plus,
  Radio,
  Settings,
  Sofa,
  Trash2,
  Trees,
  Type,
  Utensils,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChecklistDuplicator } from "@/components/checklists/ChecklistDuplicator";
import { ComponentBuilder } from "@/components/checklists/ComponentBuilder";
import { DeleteChecklistModal } from "@/components/checklists/DeleteChecklistModal";
import { DeleteComponentModal } from "@/components/checklists/DeleteComponentModal";
import { EditChecklistModal } from "@/components/checklists/EditChecklistModal";
import { EditComponentModalEnhanced } from "@/components/checklists/EditComponentModalEnhanced";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useChecklistMutations } from "@/hooks/checklist-management/useChecklistMutations";
import { useComponentMutations } from "@/hooks/checklist-management/useComponentMutations";
import { useServiceMutations } from "@/hooks/checklist-management/useServiceMutations";
import { devLog } from "@/lib/env";
import type {
  ChecklistComponent,
  ComponentOption,
  RenovationService,
  ServiceChecklist,
} from "@/types/components";

// Type definitions moved to @/types/components

// Icon mapping
const iconMap: Record<string, React.ElementType> = {
  bath: Bath,
  utensils: Utensils,
  bed: Bed,
  sofa: Sofa,
  trees: Trees,
  home: Home,
};

const componentTypeIcons: Record<string, React.ElementType> = {
  text: Type,
  number: Hash,
  textarea: AlignLeft,
  select: List,
  checkbox: CheckSquare,
  radio: Radio,
};

// Sortable Checklist Item Component
function SortableChecklistItem({
  checklist,
  isSelected,
  onSelect,
  onEdit,
  onDuplicate,
  onDelete,
  isDeletingChecklist,
}: {
  checklist: ServiceChecklist;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  isDeletingChecklist: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: checklist.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`cursor-pointer transition-colors ${
        isSelected ? "border-primary bg-primary/5" : "hover:bg-accent/50"
      }`}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-1">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="font-medium">{checklist.name}</p>
              {checklist.description && (
                <p className="text-sm text-muted-foreground mt-1">{checklist.description}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title="Edit checklist"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              title="Duplicate checklist"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={isDeletingChecklist}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              title="Delete checklist"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Sortable Component Item Component
function SortableComponentItem({
  component,
  onEdit,
  onDelete,
  isDeletingComponent,
}: {
  component: ChecklistComponent;
  onEdit: () => void;
  onDelete: () => void;
  isDeletingComponent: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: component.id!,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const ComponentTypeIcon = componentTypeIcons[component.component_type] || Type;

  return (
    <Card key={component.id} ref={setNodeRef} style={style}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-1">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex flex-col items-center gap-1">
            <ComponentTypeIcon className="h-4 w-4" />
            {(component.base_price && component.base_price > 0) ||
            (component.options && component.options.some((o) => (o.price_modifier || 0) > 0)) ? (
              <DollarSign className="h-3 w-3 text-green-600" />
            ) : null}
            {component.allow_quantity_input && <Hash className="h-3 w-3 text-blue-600" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium">{component.label}</p>
              {component.is_required && (
                <Badge variant="secondary" className="text-xs">
                  Required
                </Badge>
              )}
              {component.allow_quantity_input && (
                <Badge variant="outline" className="text-xs bg-blue-50">
                  Qty
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Field: {component.field_name} • Type: {component.component_type}
              {component.base_price && component.base_price > 0 && (
                <span className="ml-2">
                  • Base: ${component.base_price.toFixed(2)}
                  {component.pricing_type === "per_unit" && component.pricing_unit_label && (
                    <span> {component.pricing_unit_label}</span>
                  )}
                </span>
              )}
            </p>
            {component.help_text && (
              <p className="text-sm text-muted-foreground mt-2">{component.help_text}</p>
            )}
            {component.options && component.options.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium">Options:</p>
                {component.options.map((option, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    <span>• {option.option_label}</span>
                    {(option.price_modifier || 0) > 0 && (
                      <span className="text-green-600 font-medium">
                        ${(option.price_modifier || 0).toFixed(2)}
                      </span>
                    )}
                    {Boolean(option.is_default) && (
                      <Badge variant="outline" className="text-xs">
                        Default
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} title="Edit component">
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={isDeletingComponent}
              onClick={onDelete}
              title="Delete component"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ChecklistManagement() {
  const [selectedService, setSelectedService] = useState<RenovationService | null>(null);
  const [selectedChecklist, setSelectedChecklist] = useState<ServiceChecklist | null>(null);
  const [isAddingChecklist, setIsAddingChecklist] = useState(false);
  const [isEditingChecklist, setIsEditingChecklist] = useState(false);
  const [isAddingComponent, setIsAddingComponent] = useState(false);
  const [isDuplicatingChecklist, setIsDuplicatingChecklist] = useState(false);
  const [checklistToDuplicate, setChecklistToDuplicate] = useState<ServiceChecklist | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [componentToDelete, setComponentToDelete] = useState<ChecklistComponent | null>(null);
  const [isDeleteChecklistModalOpen, setIsDeleteChecklistModalOpen] = useState(false);
  const [checklistToDelete, setChecklistToDelete] = useState<ServiceChecklist | null>(null);
  const [isEditChecklistModalOpen, setIsEditChecklistModalOpen] = useState(false);
  const [checklistToEdit, setChecklistToEdit] = useState<ServiceChecklist | null>(null);
  const [isEditComponentModalOpen, setIsEditComponentModalOpen] = useState(false);
  const [componentToEdit, setComponentToEdit] = useState<ChecklistComponent | null>(null);
  const [newChecklistName, setNewChecklistName] = useState("");
  const [newChecklistDescription, setNewChecklistDescription] = useState("");
  const [newChecklistWallByWall, setNewChecklistWallByWall] = useState(false);

  // Service management states
  const [isAddingService, setIsAddingService] = useState(false);
  const [isEditingService, setIsEditingService] = useState(false);
  const [serviceToEdit, setServiceToEdit] = useState<RenovationService | null>(null);
  const [serviceToDelete, setServiceToDelete] = useState<RenovationService | null>(null);
  const [newServiceName, setNewServiceName] = useState("");
  const [newServiceDescription, setNewServiceDescription] = useState("");
  const [newServiceIcon, setNewServiceIcon] = useState("home");

  const queryClient = useQueryClient();

  const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch services
  const { data: services = [] } = useQuery<RenovationService[]>({
    queryKey: ["renovation-services"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/services`);
      if (!response.ok) throw new Error("Failed to fetch services");
      const result = await response.json();
      return result.data;
    },
  });

  // Fetch checklists for selected service
  const { data: checklists = [] } = useQuery<ServiceChecklist[]>({
    queryKey: ["service-checklists", selectedService?.id],
    queryFn: async () => {
      if (!selectedService) return [];
      const response = await fetch(`${apiUrl}/api/v1/checklists/service/${selectedService.id}`);
      if (!response.ok) throw new Error("Failed to fetch checklists");
      const result = await response.json();
      return result.data;
    },
    enabled: !!selectedService,
  });

  // Fetch components for selected checklist
  const { data: components = [] } = useQuery<ChecklistComponent[]>({
    queryKey: ["checklist-components", selectedChecklist?.id],
    queryFn: async () => {
      if (!selectedChecklist) return [];
      const response = await fetch(
        `${apiUrl}/api/v1/checklists/${selectedChecklist.id}/components`
      );
      if (!response.ok) throw new Error("Failed to fetch components");
      const result = await response.json();
      return result.data;
    },
    enabled: !!selectedChecklist,
  });

  // Use custom hooks for mutations
  const serviceMutations = useServiceMutations();
  const checklistMutations = useChecklistMutations(selectedService?.id);
  const componentMutations = useComponentMutations(selectedChecklist?.id);

  const handleCreateService = () => {
    if (!newServiceName.trim()) return;

    serviceMutations.createServiceMutation.mutate({
      name: newServiceName.trim(),
      description: newServiceDescription.trim(),
      icon: newServiceIcon,
    });
  };

  const handleEditService = (service: RenovationService) => {
    setServiceToEdit(service);
    setNewServiceName(service.name);
    setNewServiceDescription(service.description || "");
    setNewServiceIcon(service.icon);
    setIsEditingService(true);
  };

  const handleUpdateService = () => {
    if (!(serviceToEdit && newServiceName.trim())) return;

    serviceMutations.updateServiceMutation.mutate({
      id: serviceToEdit.id,
      name: newServiceName.trim(),
      description: newServiceDescription.trim(),
      icon: newServiceIcon,
    });
  };

  const handleDeleteService = (service: RenovationService) => {
    setServiceToDelete(service);
  };

  const confirmDeleteService = () => {
    if (serviceToDelete) {
      serviceMutations.deleteServiceMutation.mutate(serviceToDelete.id);
    }
  };

  const handleCreateChecklist = () => {
    if (!(selectedService && newChecklistName.trim())) return;

    checklistMutations.createChecklistMutation.mutate({
      serviceId: selectedService.id,
      name: newChecklistName.trim(),
      description: newChecklistDescription.trim(),
      wall_by_wall: newChecklistWallByWall,
    });
  };

  const handleDeleteComponent = (component: ChecklistComponent) => {
    setComponentToDelete(component);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteComponent = () => {
    if (componentToDelete) {
      componentMutations.deleteComponentMutation.mutate(componentToDelete.id!);
      setIsDeleteModalOpen(false);
      setComponentToDelete(null);
    }
  };

  const cancelDeleteComponent = () => {
    setIsDeleteModalOpen(false);
    setComponentToDelete(null);
  };

  const handleDeleteChecklist = (checklist: ServiceChecklist) => {
    setChecklistToDelete(checklist);
    setIsDeleteChecklistModalOpen(true);
  };

  const confirmDeleteChecklist = () => {
    if (checklistToDelete) {
      checklistMutations.deleteChecklistMutation.mutate(checklistToDelete.id);
      setIsDeleteChecklistModalOpen(false);
      setChecklistToDelete(null);
    }
  };

  const cancelDeleteChecklist = () => {
    setIsDeleteChecklistModalOpen(false);
    setChecklistToDelete(null);
  };

  const handleEditChecklist = (checklist: ServiceChecklist) => {
    setChecklistToEdit(checklist);
    setIsEditChecklistModalOpen(true);
  };

  const confirmEditChecklist = (data: {
    name: string;
    description: string;
    wall_by_wall: boolean;
  }) => {
    if (checklistToEdit) {
      checklistMutations.updateChecklistMutation.mutate({
        id: checklistToEdit.id,
        ...data,
      });
    }
  };

  const cancelEditChecklist = () => {
    setIsEditChecklistModalOpen(false);
    setChecklistToEdit(null);
  };

  const handleEditComponent = (component: ChecklistComponent) => {
    setComponentToEdit(component);
    setIsEditComponentModalOpen(true);
  };

  const confirmEditComponent = (data: any) => {
    if (componentToEdit) {
      componentMutations.updateComponentMutation.mutate({
        id: componentToEdit.id!,
        data,
      });
    }
  };

  const cancelEditComponent = () => {
    setIsEditComponentModalOpen(false);
    setComponentToEdit(null);
  };

  const ServiceIcon = ({ icon }: { icon: string }) => {
    const Icon = iconMap[icon] || Home;
    return <Icon className="h-5 w-5" />;
  };

  const ComponentTypeIcon = ({ type }: { type: string }) => {
    const Icon = componentTypeIcons[type] || Type;
    return <Icon className="h-4 w-4" />;
  };

  // Drag-and-drop handlers
  const handleChecklistDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = checklists.findIndex((item) => item.id === active.id);
      const newIndex = checklists.findIndex((item) => item.id === over.id);

      const reordered = arrayMove(checklists, oldIndex, newIndex);

      // Optimistically update the UI
      queryClient.setQueryData(["service-checklists", selectedService?.id], reordered);

      // Save to backend
      if (selectedService) {
        checklistMutations.reorderChecklistsMutation.mutate({
          serviceId: selectedService.id,
          checklistIds: reordered.map((c) => c.id),
        });
      }
    }
  };

  const handleComponentDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = components.findIndex((item) => item.id === active.id);
      const newIndex = components.findIndex((item) => item.id === over.id);

      const reordered = arrayMove(components, oldIndex, newIndex);

      // Optimistically update the UI
      queryClient.setQueryData(["checklist-components", selectedChecklist?.id], reordered);

      // Save to backend
      if (selectedChecklist) {
        componentMutations.reorderComponentsMutation.mutate({
          checklistId: selectedChecklist.id,
          componentIds: reordered.map((c) => c.id),
        });
      }
    }
  };

  return (
    <DashboardLayout>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Checklist Management</CardTitle>
          <CardDescription>
            Create and manage checklists for different renovation services
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Services List */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Services</h3>
                <Button size="sm" onClick={() => setIsAddingService(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Service
                </Button>
              </div>
              <ScrollArea className="h-[500px] border rounded-lg p-4">
                <div className="space-y-2">
                  {services.map((service) => (
                    <Card
                      key={service.id}
                      className={`cursor-pointer transition-colors group ${
                        selectedService?.id === service.id
                          ? "border-primary bg-primary/5"
                          : "hover:bg-accent/50"
                      }`}
                      onClick={() => {
                        setSelectedService(service);
                        setSelectedChecklist(null);
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <ServiceIcon icon={service.icon} />
                          <div className="flex-1">
                            <p className="font-medium">{service.name}</p>
                            <p className="text-sm text-muted-foreground">{service.description}</p>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditService(service);
                              }}
                              title="Edit service"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={serviceMutations.isDeleting}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteService(service);
                              }}
                              title="Delete service"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Checklists List */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">
                  {selectedService ? `${selectedService.name} Checklists` : "Select a Service"}
                </h3>
                {selectedService && (
                  <Button size="sm" onClick={() => setIsAddingChecklist(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Checklist
                  </Button>
                )}
              </div>
              {selectedService ? (
                <ScrollArea className="h-[500px] border rounded-lg p-4">
                  {checklists.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No checklists yet. Add your first checklist.
                    </p>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleChecklistDragEnd}
                    >
                      <SortableContext
                        items={checklists.map((c) => c.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2">
                          {checklists.map((checklist) => (
                            <SortableChecklistItem
                              key={checklist.id}
                              checklist={checklist}
                              isSelected={selectedChecklist?.id === checklist.id}
                              onSelect={() => setSelectedChecklist(checklist)}
                              onEdit={() => handleEditChecklist(checklist)}
                              onDuplicate={() => {
                                setChecklistToDuplicate(checklist);
                                setIsDuplicatingChecklist(true);
                              }}
                              onDelete={() => handleDeleteChecklist(checklist)}
                              isDeletingChecklist={checklistMutations.isDeleting}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </ScrollArea>
              ) : (
                <div className="border rounded-lg p-8 text-center text-muted-foreground">
                  Select a service to view checklists
                </div>
              )}
            </div>

            {/* Components List */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">
                  {selectedChecklist ? "Checklist Components" : "Select a Checklist"}
                </h3>
                {selectedChecklist && (
                  <Button size="sm" onClick={() => setIsAddingComponent(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Component
                  </Button>
                )}
              </div>
              {selectedChecklist ? (
                <ScrollArea className="h-[500px] border rounded-lg p-4">
                  {components.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No components yet. Add form fields to this checklist.
                    </p>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleComponentDragEnd}
                    >
                      <SortableContext
                        items={components.map((c) => c.id!)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2">
                          {components.map((component) => (
                            <SortableComponentItem
                              key={component.id}
                              component={component}
                              onEdit={() => handleEditComponent(component)}
                              onDelete={() => handleDeleteComponent(component)}
                              isDeletingComponent={componentMutations.isDeleting}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </ScrollArea>
              ) : (
                <div className="border rounded-lg p-8 text-center text-muted-foreground">
                  Select a checklist to view components
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Checklist Dialog */}
      <Dialog open={isAddingChecklist} onOpenChange={setIsAddingChecklist}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Checklist</DialogTitle>
            <DialogDescription>
              Create a new checklist for {selectedService?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="checklist-name">Checklist Name</Label>
              <Input
                id="checklist-name"
                value={newChecklistName}
                onChange={(e) => setNewChecklistName(e.target.value)}
                placeholder="e.g., Removal and disposal of tiles"
              />
            </div>
            <div>
              <Label htmlFor="checklist-description">Description (Optional)</Label>
              <Textarea
                id="checklist-description"
                value={newChecklistDescription}
                onChange={(e) => setNewChecklistDescription(e.target.value)}
                placeholder="Describe this checklist..."
              />
            </div>
            <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Checkbox
                id="wall-by-wall-checkbox"
                checked={newChecklistWallByWall}
                onCheckedChange={(checked) => setNewChecklistWallByWall(checked === true)}
              />
              <div className="flex-1">
                <Label htmlFor="wall-by-wall-checkbox" className="cursor-pointer font-medium">
                  Enable wall-by-wall area calculator
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Displays a calculator at the top of this checklist for calculating wall and floor
                  areas
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAddingChecklist(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateChecklist}>Create Checklist</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Component Builder Dialog */}
      {selectedChecklist && (
        <ComponentBuilder
          isOpen={isAddingComponent}
          onClose={() => setIsAddingComponent(false)}
          checklistId={selectedChecklist.id}
          checklistName={selectedChecklist.name}
          existingComponents={components}
        />
      )}

      {/* Checklist Duplicator Dialog */}
      {checklistToDuplicate && (
        <ChecklistDuplicator
          isOpen={isDuplicatingChecklist}
          onClose={() => {
            setIsDuplicatingChecklist(false);
            setChecklistToDuplicate(null);
          }}
          checklist={{
            id: checklistToDuplicate.id,
            name: checklistToDuplicate.name,
            service_id: checklistToDuplicate.service_id,
          }}
          services={services}
        />
      )}

      {/* Delete Component Modal */}
      <DeleteComponentModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDeleteComponent}
        onConfirm={confirmDeleteComponent}
        componentName={componentToDelete?.label || ""}
        isDeleting={componentMutations.isDeleting}
      />

      {/* Delete Checklist Modal */}
      <DeleteChecklistModal
        isOpen={isDeleteChecklistModalOpen}
        onClose={cancelDeleteChecklist}
        onConfirm={confirmDeleteChecklist}
        checklistName={checklistToDelete?.name || ""}
        isDeleting={checklistMutations.isDeleting}
      />

      {/* Edit Checklist Modal */}
      {checklistToEdit && (
        <EditChecklistModal
          isOpen={isEditChecklistModalOpen}
          onClose={cancelEditChecklist}
          onConfirm={confirmEditChecklist}
          checklist={checklistToEdit}
          isUpdating={checklistMutations.isUpdating}
        />
      )}

      {/* Edit Component Modal */}
      {componentToEdit && (
        <EditComponentModalEnhanced
          isOpen={isEditComponentModalOpen}
          onClose={cancelEditComponent}
          onConfirm={confirmEditComponent}
          component={componentToEdit}
          isUpdating={componentMutations.isUpdating}
        />
      )}

      {/* Add Service Dialog */}
      <Dialog open={isAddingService} onOpenChange={setIsAddingService}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Service</DialogTitle>
            <DialogDescription>Create a new renovation service category</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="service-name">Service Name *</Label>
              <Input
                id="service-name"
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value)}
                placeholder="e.g., Bathroom, Kitchen, Living Room"
              />
            </div>
            <div>
              <Label htmlFor="service-description">Description</Label>
              <Textarea
                id="service-description"
                value={newServiceDescription}
                onChange={(e) => setNewServiceDescription(e.target.value)}
                placeholder="Brief description of this service category"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="service-icon">Icon</Label>
              <Select value={newServiceIcon} onValueChange={setNewServiceIcon}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bath">Bath (Bathroom)</SelectItem>
                  <SelectItem value="utensils">Utensils (Kitchen)</SelectItem>
                  <SelectItem value="bed">Bed (Bedroom)</SelectItem>
                  <SelectItem value="sofa">Sofa (Living Room)</SelectItem>
                  <SelectItem value="trees">Trees (Outdoor)</SelectItem>
                  <SelectItem value="home">Home (General)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAddingService(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateService}
                disabled={!newServiceName.trim() || serviceMutations.isCreating}
              >
                {serviceMutations.isCreating ? "Creating..." : "Create Service"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={isEditingService} onOpenChange={setIsEditingService}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Service</DialogTitle>
            <DialogDescription>Update service information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-service-name">Service Name *</Label>
              <Input
                id="edit-service-name"
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value)}
                placeholder="e.g., Bathroom, Kitchen, Living Room"
              />
            </div>
            <div>
              <Label htmlFor="edit-service-description">Description</Label>
              <Textarea
                id="edit-service-description"
                value={newServiceDescription}
                onChange={(e) => setNewServiceDescription(e.target.value)}
                placeholder="Brief description of this service category"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="edit-service-icon">Icon</Label>
              <Select value={newServiceIcon} onValueChange={setNewServiceIcon}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bath">Bath (Bathroom)</SelectItem>
                  <SelectItem value="utensils">Utensils (Kitchen)</SelectItem>
                  <SelectItem value="bed">Bed (Bedroom)</SelectItem>
                  <SelectItem value="sofa">Sofa (Living Room)</SelectItem>
                  <SelectItem value="trees">Trees (Outdoor)</SelectItem>
                  <SelectItem value="home">Home (General)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditingService(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateService}
                disabled={!newServiceName.trim() || serviceMutations.isUpdating}
              >
                {serviceMutations.isUpdating ? "Updating..." : "Update Service"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Service Confirmation */}
      <Dialog open={!!serviceToDelete} onOpenChange={() => setServiceToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Service</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{serviceToDelete?.name}"? This will also delete all
              associated checklists and components. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setServiceToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteService}
              disabled={serviceMutations.isDeleting}
            >
              {serviceMutations.isDeleting ? "Deleting..." : "Delete Service"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
